import { AddressTypeEnum } from "@lib/enum"
import { ignoreBodyByModel } from "@models/global"

export class CustomerPotentialModel {
  id: any
  constructor() {
    this.id = undefined
  }

  public static assign(obj) {
    if (!obj) return undefined

    const newObj = Object.assign(new CustomerPotentialModel(), obj)

    newObj.currentAddress = obj.customerAddresses?.find(
      (item) => item.addressType === AddressTypeEnum.CURRENT
    )
    newObj.permanentAddress = obj.customerAddresses?.find(
      (item) => item.addressType === AddressTypeEnum.PERMANENT
    )
    newObj.hasCurrentAddress = !!newObj.currentAddress
    newObj.tags = obj.tags?.map((tag) => ({
      label: tag,
      value: tag,
    }))
    newObj.areas = obj.areas?.map((area) => ({
      label: area?.provinceName,
      value: area?.id,
    }))
    newObj.townshipAreaIds = obj.townshipAreas?.map((item) => ({
      label: item?.name,
      value: item?.id,
    }))
    newObj.projects = obj.projects?.map((project) => ({
      label: project?.projectName,
      value: project?.id,
    }))
    newObj.propertyTypes = obj.propertyTypes?.map((item) => ({
      label: item?.name,
      value: item?.id,
    }))
    newObj.townships = obj.townships?.map((township) => ({
      label: township?.name,
      value: township?.id,
    }))
    newObj.primaryPhone =
      obj.customerPhoneNumbers?.find((phone) => phone.isPrimary)?.phoneNumber ??
      undefined
    newObj.primaryEmail =
      obj.customerEmailAddresses?.find((email) => email.isPrimary)
        ?.emailAddress ?? undefined
    // Inquiry integration: group API inquiries by groupId → IInquiryGroup[] cho form
    newObj.inquiryCount = obj.inquiryCount ?? 0
    const inquiries = obj.inquiries ?? []
    const groupMap = new Map<string, any>()
    inquiries.forEach((item: any, index: number) => {
      const gid = item.groupId ?? `solo-${item.id ?? index}`
      if (!groupMap.has(gid)) {
        groupMap.set(gid, {
          key: gid,
          groupId: item.groupId ?? undefined,
          inquiryTypeId: item.inquiryTypeId ?? item.inquiryType?.id,
          inquiryTypeName: item.inquiryTypeName ?? item.inquiryType?.description ?? item.inquiryType?.name,
          rows: [],
        })
      }
      const ptMap = item.inquiryPropertyTypeMap ?? []
      const vMap = item.inquiryViewMap ?? []
      groupMap.get(gid).rows.push({
        key: item.id != null ? String(item.id) : `inq-${index}`,
        projectId: item.inquiryProjectMap?.[0]?.id,
        projectName: item.inquiryProjectMap?.[0]?.projectName ?? item.inquiryProjectMap?.[0]?.name,
        unitName: item.productName ?? item.inquiryName,
        propertyTypeIds: ptMap.map((p: any) => p.id),
        propertyTypeNames: ptMap.map((p: any) => p.name).join(', ') || undefined,
        viewIds: vMap.map((v: any) => v.id),
        viewNames: vMap.map((v: any) => v.name).join(', ') || undefined,
        fromPrice: item.fromPrice ?? undefined,
        toPrice: item.toPrice ?? undefined,
        description: item.description ?? undefined,
      })
    })
    newObj.sampleInquiries = Array.from(groupMap.values())
    return newObj
  }

  public static assigns<T>(objs) {
    const results: any[] = []
    objs?.forEach((item) => results.push(this.assign(item)))
    return results
  }
}

export class CustomerPotentialUpdateModel {
  id: any
  constructor() {
    this.id = undefined
  }

  public static assign(obj) {
    if (!obj) return undefined

    const newObj = Object.assign(new CustomerPotentialUpdateModel(), obj)
    newObj.tags = obj.tags?.map((tag) => tag.value)
    newObj.areaIds = obj.areas?.map((area) => area?.value)
    newObj.townshipAreaIds = obj.townshipAreaIds?.map((item) => item?.value)
    newObj.projectIds = obj.projects?.map((project) => project?.value)
    newObj.propertyTypeIds = obj.propertyTypes?.map((item) => item?.value)
    newObj.townshipIds = obj.townships?.map((township) => township?.value)

    newObj.customerAddresses = [
      obj.currentAddress
        ? { addressType: AddressTypeEnum.CURRENT, ...obj.currentAddress }
        : undefined,
      obj.permanentAddress
        ? { addressType: AddressTypeEnum.PERMANENT, ...obj.permanentAddress }
        : undefined,
    ].filter((item) => item !== undefined)
    // sampleInquiries chỉ dùng khi CREATE customer (BE không hỗ trợ ở Update)
    // Khi edit, inquiry được quản lý qua Inquiry API riêng
    if (!obj.id) {
      // Flatten IInquiryGroup[] → SampleInquiryDto[] với groupIndex per group
      const groups = obj.sampleInquiries ?? []
      const flatList: any[] = []
      groups.forEach((group: any, groupIdx: number) => {
        const rows = group.rows ?? []
        rows.forEach((row: any) => {
          flatList.push({
            inquiryTypeId: group.inquiryTypeId,
            projectId: row.projectId,
            productName: (row.unitName ?? row.productName)?.trim() || undefined,
            // SampleInquiryDto chỉ nhận singular — lấy phần tử đầu
            propertyTypeId: row.propertyTypeIds?.[0] ?? row.propertyTypeId,
            view: row.viewIds?.[0] != null
              ? String(row.viewIds[0])
              : row.viewId != null
                ? String(row.viewId)
                : undefined,
            description: row.description?.trim() || undefined,
            groupIndex: groupIdx,
          })
        })
      })
      newObj.sampleInquiries = flatList.length > 0 ? flatList : undefined
    } else {
      delete newObj.sampleInquiries
    }
    return newObj
  }

  public static assigns<T>(objs) {
    const results: any[] = []
    objs?.forEach((item) => results.push(this.assign(item)))
    return results
  }
}

export class CustomerBookingUpdateModel {
  id?: number
  sourceId?: any
  type?: any
  firstName?: any
  lastName?: any
  fullName?: any
  shortName?: any
  birthDate?: any
  gender?: any
  identifierCardNumber?: any
  idIssuedDate?: any
  idIssuedPlace?: any
  nationalityId?: any
  represenFullName?: any
  represenIdentifierCardNumber?: any
  represenPhoneNumber?: any
  represenEmail?: any
  represenDescription?: any
  isRepresen?: any
  isCompany?: any
  companyName?: any
  companyTaxCode?: any
  companyAddress?: any
  customerAddresses?: any[]
  customerPhoneNumbers?: any[]
  customerEmailAddresses?: any[]
  //
  repCountryId?: any
  repProvinceId?: any
  repDistrictId?: any
  repIdIssuedDate?: any
  repIdIssuedPlace?: any
  repCompanyTaxCode?: any
  repBirthDate?: any
  repNationalityId?: any
  customerType?: any
  represenEmails?: any
  represenPhoneNumbers?: any
  represenIdIssuedDate?: any
  represenIdIssuedPlace?: any
  represenNationalityId?: any
  legalRepresentative?: any
  position?: any
  legalRepresentativeIdentityCardNumber?: any
  legalRepresentativeIdIssueDate?: any
  legalRepresentativeIdIssuePlace?: any

  beneficiary?: any
  bankNumber?: any
  bankName?: any
  bankBranch?: any
  description?: any

  constructor() {}
  public static assign(obj) {
    if (!obj) return undefined

    const newObj = Object.assign(new CustomerBookingUpdateModel(), obj)
    newObj.customerAddresses = [
      obj.currentAddress
        ? { addressType: AddressTypeEnum.CURRENT, ...obj.currentAddress }
        : undefined,
      obj.permanentAddress
        ? { addressType: AddressTypeEnum.PERMANENT, ...obj.permanentAddress }
        : undefined,
    ].filter((item) => item !== undefined)
    return ignoreBodyByModel(newObj, CustomerBookingUpdateModel)
  }

  public static assigns<T>(objs) {
    const results: any[] = []
    objs?.forEach((item) => results.push(this.assign(item)))
    return results
  }
}
